
import React, { useState, useEffect, Suspense } from 'react';
import { Dealer } from '@/types/dealer';
import { isSSR } from '@/lib/isSSR';

interface MapPlaceholderProps {
  dealers: Dealer[];
  userPosition?: GeolocationPosition | null;
  coordinates?: [number, number];
  height?: string;
  width?: string;
  zoom?: number;
  className?: string;
  mapId?: string;
}

export function MapPlaceholder({
  dealers,
  userPosition,
  coordinates,
  height = '400px',
  width = '100%',
  zoom = 13,
  className = '',
  mapId = 'map'
}: MapPlaceholderProps) {
  const [isClient, setIsClient] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [MapComponent, setMapComponent] = useState<React.ComponentType<any> | null>(null);

  console.log('🗺️ MapPlaceholder render:', {
    dealersCount: dealers.length,
    userPosition: userPosition ? [userPosition.coords.latitude, userPosition.coords.longitude] : null,
    coordinates,
    zoom,
    mapId,
    isClient
  });

  // Verifica che siamo lato client
  useEffect(() => {
    if (!isSSR()) {
      setIsClient(true);
    }
  }, []);

  // Carica i componenti della mappa solo lato client
  useEffect(() => {
    if (!isClient) return;

    let mounted = true;

    const loadMap = async () => {
      try {
        console.log('🔄 Loading map components...');
        
        // Importa i componenti in modo lazy
        const { ClientMap } = await import('./ClientMap');
        
        if (!mounted) return;
        
        console.log('✅ Map components loaded successfully');
        setMapComponent(() => ClientMap);
      } catch (error) {
        console.error('❌ Error loading map components:', error);
        if (mounted) {
          setMapError('Errore nel caricamento della mappa');
        }
      }
    };

    loadMap();

    return () => {
      mounted = false;
    };
  }, [isClient]);

  // Calcola il centro della mappa
  const getMapCenter = (): [number, number] => {
    if (coordinates) return coordinates;
    if (userPosition) {
      return [userPosition.coords.latitude, userPosition.coords.longitude];
    }
    if (dealers.length > 0) {
      const dealerWithCoords = dealers.find(d => d.latitude && d.longitude);
      if (dealerWithCoords) {
        return [dealerWithCoords.latitude, dealerWithCoords.longitude];
      }
    }
    return [41.9028, 12.4964]; // Default Roma
  };

  // Renderizza durante SSR o caricamento
  if (!isClient || !MapComponent) {
    return (
      <div 
        className={`flex flex-col items-center justify-center bg-gray-100 rounded-lg animate-pulse ${className}`}
        style={{ height, width }}
      >
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-2"></div>
        <p className="text-gray-600 text-sm">Caricamento mappa...</p>
        {userPosition && (
          <p className="text-gray-500 text-xs mt-1">Posizione utente rilevata</p>
        )}
      </div>
    );
  }

  // Renderizza errore se presente
  if (mapError) {
    return (
      <div 
        className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`}
        style={{ height, width }}
      >
        <div className="text-center text-gray-600">
          <p className="mb-2">❌ {mapError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="text-blue-600 hover:underline"
          >
            Ricarica pagina
          </button>
        </div>
      </div>
    );
  }

  const mapCenter = getMapCenter();
  const mapZoom = Math.min(Math.max(zoom, 8), 18);

  return (
    <div className={`relative ${className}`} style={{ height, width }}>
      <MapComponent
        center={mapCenter}
        zoom={mapZoom}
        dealers={dealers}
        userPosition={userPosition}
        height={height}
        width={width}
      />
    </div>
  );
}
